// ============================================================
// Summit League v2 — Pagination Component
// Reusable pagination controls for leaderboard, match browse, etc.
// ============================================================

import React from 'react';

// ── Types ──────────────────────────────────────────────────

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  loading?: boolean;
  /** Label for the total count, e.g. "teams", "matches" */
  itemLabel?: string;
}

// ── Component ──────────────────────────────────────────────

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  loading = false,
  itemLabel = 'items',
}) => {
  const { total, page, totalPages, hasNextPage, hasPrevPage } = pagination;

  // Don't render if only one page
  if (totalPages <= 1 && total > 0) {
    return (
      <div className="sl-pagination">
        <span className="sl-pagination-info">
          {total} {total === 1 ? itemLabel.replace(/s$/, '') : itemLabel}
        </span>
      </div>
    );
  }

  // Don't render if no data
  if (total === 0) return null;

  return (
    <div className="sl-pagination">
      <span className="sl-pagination-info">
        {total} {total === 1 ? itemLabel.replace(/s$/, '') : itemLabel}
      </span>

      <div className="sl-pagination-controls">
        <button
          type="button"
          className="sl-pagination-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrevPage || loading}
          aria-label="Previous page"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M8.5 3L4.5 7l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Prev</span>
        </button>

        <span className="sl-pagination-page">
          Page {page} of {totalPages}
        </span>

        <button
          type="button"
          className="sl-pagination-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || loading}
          aria-label="Next page"
        >
          <span>Next</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M5.5 3l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;